import { dealDamage, healDamage, addModifier, healthRemove } from '../player.type';
import { invokeDie } from '../die.type';
import type { Damage } from '../damage.type';
import type { Die } from '../die.type';
import { selectDie, selectFace } from '../../modals/modals';
import { createDie } from '../die.type';
import { lockedDie, activePlayer, inactivePlayer } from '../../../store';
import type { Technique, Requirement } from '../techniques.type';
import type { Player } from '../player.type';
import type { Ammo } from '../status-effects/ammo';
import { StatusEffect } from '../status-effects.type';
import { DieFace } from '../face.type';
import { SoundType } from '../../components/audio.svelte';
import { AmmoType } from '../ammo.type';

export interface AmmoOutput {
  ammoType: AmmoType;
  damage: Damage;
  statusEffects: Array<{ type: StatusEffect; stacks: number }>;
}

export const getAmmoEffect = (
  target: Player,
  source: Player,
  dice: Array<Die>,
): AmmoOutput | undefined => {
  const ammo: Ammo = source.statusEffects.find((modifier) => {
    return modifier.type == StatusEffect.Ammo;
  }) as Ammo;

  let type: AmmoType = ammo.getAmmo();
  if (type === undefined) {
    return;
  }

  let output: AmmoOutput = {
    ammoType: type,
    damage: { value: 0, target, source, dice },
    statusEffects: [],
  };

  switch (type) {
    case AmmoType.Poison:
      output.damage.value = 1;
      output.statusEffects.push({ type: StatusEffect.Poison, stacks: 1 });
      break;

    case AmmoType.Blood:
      output.damage.value = 1;
      output.statusEffects.push({ type: StatusEffect.Bleed, stacks: 1 });
      break;

    case AmmoType.Blind:
      output.damage.value = 1;
      output.statusEffects.push({ type: StatusEffect.Blind, stacks: 1 });
      break;

    case AmmoType.Web:
      output.damage.value = 1;
      output.statusEffects.push({ type: StatusEffect.Snared, stacks: 1 });
      break;

    case AmmoType.Burning:
      output.damage.value = 1;
      output.statusEffects.push({ type: StatusEffect.Burning, stacks: 1 });
      break;

    case AmmoType.Fist:
      output.damage.value = 3;
      break;

    case AmmoType.Silver:
      output.damage.value = 4;
      break;

    case AmmoType.Blank:
    default:
      // dealDamage({value: 2, target, source, dice});

      output.damage.value = 2;
      break;
  }

  return output;
};

export function shootAmmo(ammo: AmmoOutput, target: Player) {
  if (ammo) {
    if (ammo.damage.value > 0) {
      dealDamage(ammo.damage);
    }
    ammo.statusEffects.forEach((statusEffect) => {
      addModifier(target, statusEffect.type as StatusEffect, statusEffect.stacks);
    });
  }
}

export const hasAmmo: Requirement = {
  text: 'Requires Ammo',
  // iconBonus: '',
  icon: 'ammo',
  test: (source: Player, target: Player) => {
    const ammo = source.statusEffects.find((modifier) => {
      return modifier.type == StatusEffect.Ammo;
    });
    return !!ammo;
  },
};

export const ammo: Technique[] = [
  {
    ingredients: [DieFace.Ammo],
    title: 'Rearm',
    text: 'Gain 1 Ammo (Basic).',
    tags: ['buff', 'ammo'],
    audio: SoundType.None,
    activate: (target: Player, source: Player, dice: Array<Die>) => {
      addModifier(source, StatusEffect.Ammo, 1, { ammoType: AmmoType.Blank });
    },
  },
  {
    ingredients: [DieFace.Ammo, DieFace.Ammo],
    title: 'Restock',
    text: 'Gain 3 Ammo (Basic).',
    tags: ['buff', 'ammo'],
    audio: SoundType.None,
    activate: (target: Player, source: Player, dice: Array<Die>) => {
      addModifier(source, StatusEffect.Ammo, 3, { ammoType: AmmoType.Blank });
    },
  },
  {
    ingredients: [DieFace.Ammo, DieFace.Ammo, DieFace.Ammo],
    title: 'Refill',
    text: 'Gain 7 Ammo (Basic).',
    tags: ['buff', 'ammo'],
    audio: SoundType.None,
    activate: (target: Player, source: Player, dice: Array<Die>) => {
      addModifier(source, StatusEffect.Ammo, 7, { ammoType: AmmoType.Blank });
    },
  },
  {
    ingredients: [DieFace.Ammo, DieFace.Poison],
    title: 'Poisoned Ammo',
    text: 'Gain 2 Ammo (Poison).',
    tags: ['buff', 'ammo'],
    audio: SoundType.None,
    activate: (target: Player, source: Player, dice: Array<Die>) => {
      addModifier(source, StatusEffect.Ammo, 2, { ammoType: AmmoType.Poison });
    },
  },
  {
    ingredients: [DieFace.Ammo, DieFace.Blood],
    title: 'Serrated Ammo',
    text: 'Gain 2 Ammo (Serrated).',
    tags: ['buff', 'ammo'],
    audio: SoundType.None,
    activate: (target: Player, source: Player, dice: Array<Die>) => {
      addModifier(source, StatusEffect.Ammo, 2, { ammoType: AmmoType.Blood });
    },
  },
  {
    ingredients: [DieFace.Ammo, DieFace.Eye],
    title: 'Blinding Ammo',
    text: 'Gain 2 Ammo (Blinding).',
    tags: ['buff', 'ammo'],
    audio: SoundType.None,
    activate: (target: Player, source: Player, dice: Array<Die>) => {
      addModifier(source, StatusEffect.Ammo, 2, { ammoType: AmmoType.Blind });
    },
  },
  {
    ingredients: [DieFace.Ammo, DieFace.Agility],
    title: 'Crippling Ammo',
    text: 'Gain 2 Ammo (Crippling).',
    tags: ['buff', 'ammo'],
    audio: SoundType.None,
    activate: (target: Player, source: Player, dice: Array<Die>) => {
      addModifier(source, StatusEffect.Ammo, 2, { ammoType: AmmoType.Web });
    },
  },
  {
    ingredients: [DieFace.Ammo, DieFace.Fire],
    title: 'Burning Ammo',
    text: 'Gain 2 Ammo (Burning).',
    tags: ['buff', 'ammo'],
    audio: SoundType.None,
    activate: (target: Player, source: Player, dice: Array<Die>) => {
      addModifier(source, StatusEffect.Ammo, 2, { ammoType: AmmoType.Burning });
    },
  },
  {
    ingredients: [DieFace.Ammo, DieFace.Fist],
    title: 'Heavy Ammo',
    text: 'Gain 2 Ammo (Heavy).',
    tags: ['buff', 'ammo'],
    audio: SoundType.None,
    activate: (target: Player, source: Player, dice: Array<Die>) => {
      addModifier(source, StatusEffect.Ammo, 2, { ammoType: AmmoType.Fist });
    },
  },
];
